import * as Icons from '@ant-design/icons';
import { Alert, Button, Col, Space, Typography } from 'antd';
import { IDocumentation } from 'interfaces/documentation/documentations';
import { IUser } from 'interfaces/users';
import {
  getUserDocumentation,
  handleDownloadDeclaration,
  hasPersonalInfo
} from 'services/report';
import { useCallback, useEffect, useState } from 'react';
import { useNotification } from '@refinedev/core';

export interface TabDeclarationsProps {
  courseEnrollmentId: string;
  user: IUser;
  completedAt?: string | null;
}

const Warnings = {
  personalInfo:
    'Para que você consiga emitir as suas declarações ou seu certificado, é necessário que o(s) campo(s) de CPF e nome completo do aluno estejam cadastrados.',
  documentation:
    'Para que você consiga emitir os certificados e declarações, é necessário que a documentação esteja aprovada.'
};

export const TabDeclarations = ({
  courseEnrollmentId,
  user,
  completedAt
}: TabDeclarationsProps) => {
  const { open } = useNotification();
  const [userDocumentation, setUserDocumentation] = useState<IDocumentation>();

  const getDocumentation = useCallback(async () => {
    const documentation = await getUserDocumentation(courseEnrollmentId, open!);
    setUserDocumentation(documentation?.[0]);
  }, [courseEnrollmentId, open]);

  useEffect(() => {
    getDocumentation();
  }, [getDocumentation]);

  const handleDownloadAcademicRecord = useCallback(() => {
    handleDownloadDeclaration(courseEnrollmentId, open!);
  }, [open, courseEnrollmentId]);

  const handleDownloadEnrollmentStatement = useCallback(() => {
    handleDownloadDeclaration(
      courseEnrollmentId,
      open!,
      'student_graduate_enrollment_statement'
    );
  }, [open, courseEnrollmentId]);

  const handleDownloadCourseFinished = useCallback(() => {
    handleDownloadDeclaration(
      courseEnrollmentId,
      open!,
      'student_graduate_course_finished'
    );
  }, [open, courseEnrollmentId]);

  return (
    <Col>
      <Space className="mb-2" direction="vertical" size="small">
        {!hasPersonalInfo(user) && (
          <Alert
            showIcon
            type="warning"
            className="text-sm"
            message={Warnings.personalInfo}
          />
        )}
        {userDocumentation?.status !== 'approved' && (
          <Alert
            showIcon
            type="warning"
            className="text-sm"
            message={Warnings.documentation}
          />
        )}
      </Space>
      <div className="flex items-center justify-between gap-3 p-3 rounded-lg border text-sm">
        <Space size="small" className="flex-1 min-w-0">
          <Icons.FileTextOutlined className="text-base text-neutral-500 shrink-0" />
          <Typography.Text className="font-medium">
            Declaração de histórico acadêmico
          </Typography.Text>
        </Space>
        <Button
          size="small"
          disabled={
            !hasPersonalInfo(user) || userDocumentation?.status !== 'approved'
          }
          icon={<Icons.DownloadOutlined />}
          onClick={handleDownloadAcademicRecord}
        >
          Emitir
        </Button>
      </div>
      <div className="flex items-center justify-between gap-3 p-3 rounded-lg border mt-2 text-sm">
        <Space size="small" className="flex-1 min-w-0">
          <Icons.IdcardOutlined className="text-base text-neutral-500 shrink-0" />
          <Typography.Text className="font-medium">
            Declaração de matrícula
          </Typography.Text>
        </Space>
        <Button
          size="small"
          disabled={
            !hasPersonalInfo(user) || userDocumentation?.status !== 'approved'
          }
          icon={<Icons.DownloadOutlined />}
          onClick={handleDownloadEnrollmentStatement}
        >
          Emitir
        </Button>
      </div>
      <div className="flex items-center justify-between gap-3 p-3 rounded-lg border mt-2 text-sm">
        <Space size="small" className="flex-1 min-w-0">
          <Icons.TrophyOutlined className="text-base text-neutral-500 shrink-0" />
          <Typography.Text className="font-medium">
            Declaração de conclusão de curso
          </Typography.Text>
        </Space>
        <Button
          size="small"
          disabled={
            !hasPersonalInfo(user) ||
            userDocumentation?.status !== 'approved' ||
            !completedAt
          }
          icon={<Icons.DownloadOutlined />}
          onClick={handleDownloadCourseFinished}
        >
          Emitir
        </Button>
      </div>
    </Col>
  );
};
